import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { api } from '../lib/utils';

/**
 * StorefrontHead — layout-level component that ensures every merchant storefront
 * page shows the merchant's favicon, title, and description.
 * Never shows the default Woyof branding on merchant pages.
 *
 * Favicon fallback: favicon_url → logo_url → blank SVG.
 * Also directly patches the DOM <link rel="icon"> since Helmet may not
 * replace the original index.html tag.
 */

const BLANK_FAVICON = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';

export const StorefrontHead = ({ subdomain }) => {
  const [store, setStore] = useState(null);

  useEffect(() => {
    if (!subdomain) return;
    let cancelled = false;
    api.get(`/public/stores/${subdomain}`).then(res => {
      if (!cancelled) setStore(res.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [subdomain]);

  // Directly patch all favicon link elements in the DOM
  useEffect(() => {
    if (!store) return;
    const faviconUrl = store.theme_settings?.favicon_url || store.logo_url || BLANK_FAVICON;
    document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach(link => {
      link.href = faviconUrl;
    });
  }, [store]);

  if (!store) return null;

  const storeName = store.name || subdomain;
  const description = store.description || `${storeName} - Order online`;
  const faviconUrl = store.theme_settings?.favicon_url || store.logo_url || BLANK_FAVICON;

  return (
    <Helmet>
      <title>{storeName}</title>
      <meta name="description" content={description} />
      <link rel="icon" href={faviconUrl} />
      <link rel="apple-touch-icon" href={faviconUrl} />
    </Helmet>
  );
};
